import { Badge, Grid, Table } from "@mantine/core";
import ReactApexChart from "react-apexcharts";
import { useState } from "react";
import useSWR from "swr";
import AdminKpiCard from "../AdminKpiCard/AdminKpiCard";
import AdminInlineFilters from "../AdminInlineFilters/AdminInlineFilters";
import classes from "./AdminDashboardView.module.css";
import {
  buildDashboardSectionQueryString,
  buildDashboardSummaryQueryString,
  buildWeeklyTrendQueryString,
  dashboardSwrKey,
} from "../../utils/dashboardQueryKeys";
import {
  formatCompactInr,
  formatDonutPercentTotal,
  formatOptionalPercent,
} from "../../utils/dashboardFormat";
import { DASHBOARD_EMPTY_STATE } from "../../constants";
import { fetcher } from "../../../../services/fetcher";

const FONT = "Inter, -apple-system, BlinkMacSystemFont, sans-serif";
const chartBase = {
  chart: { toolbar: { show: false }, animations: { enabled: false }, fontFamily: FONT },
  grid: { borderColor: "#eef0f4", strokeDashArray: 4, padding: { left: 2, right: 8 } },
  dataLabels: { enabled: false },
  tooltip: { theme: "light", style: { fontSize: "12px", fontFamily: FONT } },
  legend: { fontSize: "12px", fontFamily: FONT },
};

const truncate = (s, n = 20) => { const str = String(s ?? ""); return str.length > n ? `${str.substring(0, n)}…` : str; };

const fmtDonutCenterTotal = (total, isPercent) => {
  const n = Number(total);
  if (!Number.isFinite(n)) return "—";
  if (!isPercent) return formatCompactInr(n);
  return formatDonutPercentTotal(n);
};
const AdminRevenueTab = ({ filters, timeFrame = "W", scope = "both" }) => {
  const [operatorRevDates, setOperatorRevDates] = useState({ startDate: null, endDate: null });
  const [vendorRevDates, setVendorRevDates] = useState({ startDate: null, endDate: null });
  const [gatewayRevDates, setGatewayRevDates] = useState({ startDate: null, endDate: null });

  const summaryCanon = buildDashboardSummaryQueryString({ timeFrame, scope, filters });
  const weeklyTrendCanon = buildWeeklyTrendQueryString({ scope, filters });

  const revenueDashboardUrl = dashboardSwrKey("dashboard/revenue-dashboard/", summaryCanon);
  const weeklyTrendUrl = dashboardSwrKey("dashboard/weekly-trend/", weeklyTrendCanon);
  const operatorRevUrl = dashboardSwrKey(
    "dashboard/operator-revenue-contribution/",
    buildDashboardSectionQueryString(summaryCanon, operatorRevDates)
  );
  const vendorRevUrl = dashboardSwrKey(
    "dashboard/vendor-revenue-contribution/",
    buildDashboardSectionQueryString(summaryCanon, vendorRevDates)
  );
  const revenueByRegionUrl = dashboardSwrKey("dashboard/revenue-by-region/", summaryCanon);
  const gatewayRevUrl = dashboardSwrKey(
    "dashboard/gateway-wise-revenue/",
    buildDashboardSectionQueryString(summaryCanon, gatewayRevDates)
  );
  const transactionBreakdownUrl = dashboardSwrKey("dashboard/transaction-breakdown-by-gateway/", summaryCanon);

  const { data: revenueSummaryData } = useSWR(revenueDashboardUrl, fetcher);
  const { data: revenueTrendData } = useSWR(weeklyTrendUrl, fetcher);
  const { data: operatorRevenueData } = useSWR(operatorRevUrl, fetcher);
  const { data: vendorRevenueData } = useSWR(vendorRevUrl, fetcher);
  const { data: revenueByRegionData } = useSWR(revenueByRegionUrl, fetcher);
  const { data: gatewayRevenueData } = useSWR(gatewayRevUrl, fetcher);
  const { data: transactionBreakdownData } = useSWR(transactionBreakdownUrl, fetcher);

  const toRows = (payload) =>
    Array.isArray(payload) ? payload
      : Array.isArray(payload?.results) ? payload.results
      : Array.isArray(payload?.data) ? payload.data
      : [];

  const currencyBreakdownRows = Array.isArray(revenueSummaryData?.currency_breakdown)
    ? [...revenueSummaryData.currency_breakdown].sort(
      (a, b) => Number(b?.revenue ?? 0) - Number(a?.revenue ?? 0)
    )
    : [];
  const getCurrencyRow = (currencyCode) =>
    currencyBreakdownRows.find((item) => String(item?.currency || "").toUpperCase() === currencyCode) || null;

  // ── Revenue trend + bookings (weekly-trend endpoint) ──────────────────────────
  const revenueTrendRows = (() => {
    const sourceRows =
      timeFrame === "D"
        ? (Array.isArray(revenueTrendData?.points) ? revenueTrendData.points
          : Array.isArray(revenueTrendData?.data?.points) ? revenueTrendData.data.points : [])
        : timeFrame === "M"
          ? (Array.isArray(revenueTrendData?.months) ? revenueTrendData.months
            : Array.isArray(revenueTrendData?.data?.months) ? revenueTrendData.data.months : [])
          : (Array.isArray(revenueTrendData?.weeks) ? revenueTrendData.weeks
            : Array.isArray(revenueTrendData?.data?.weeks) ? revenueTrendData.data.weeks : []);
    return sourceRows.map((item) => ({
      label: item?.week_label ?? item?.month_label ?? item?.label ?? item?.date ?? item?.week ?? item?.month ?? "",
      revenue: Number(item?.revenue ?? 0),
      bookings: Number(item?.bookings ?? 0),
      start: item?.week_start ?? item?.start_date ?? null,
      end: item?.week_end ?? item?.end_date ?? null,
    }));
  })();

  const weeklyCategories = revenueTrendRows.map((item) => item.label);
  const weeklyRevenueValues = revenueTrendRows.map((item) => item.revenue);
  const weeklyBookingsValues = revenueTrendRows.map((item) => item.bookings);

  const dynamicRevenueKpis = ["INR", "NPR", "USD"].map((currency, idx) => {
    const row = getCurrencyRow(currency);
    const tone = idx === 0 ? "green" : idx === 1 ? "blue" : "amber";
    const revenue = row?.revenue;
    const hasRevenue = revenue !== undefined && revenue !== null && revenue !== "";
    return {
      label: `${currency} Revenue`,
      value: hasRevenue ? Number(revenue).toLocaleString("en-IN") : "—",
      tone,
    };
  });

  // Each endpoint wraps its array under a domain-specific key — check those first
  const operatorRows =
    Array.isArray(operatorRevenueData?.operators) ? operatorRevenueData.operators : toRows(operatorRevenueData);
  const operatorLabels = operatorRows.map((item) => item?.operator_name || item?.name || "Operator");
  const operatorValues = operatorRows.map((item) =>
    Number(item?.percentage ?? item?.contribution_value ?? item?.revenue ?? item?.value ?? 0)
  );
  const operatorPieUsesPercent = operatorRows.some(
    (item) => item?.percentage !== undefined && item?.percentage !== null && item?.percentage !== ""
  );
  const operatorPieTotal = operatorValues.reduce((sum, v) => sum + Number(v || 0), 0);

  const vendorRows =
    Array.isArray(vendorRevenueData?.vendors) ? vendorRevenueData.vendors : toRows(vendorRevenueData);
  const vendorLabels = vendorRows.map((item) => item?.vendor_name || item?.name || "Vendor");
  const vendorValues = vendorRows.map((item) =>
    Number(item?.percentage ?? item?.contribution_value ?? item?.revenue ?? item?.value ?? 0)
  );

  const regionRows =
    Array.isArray(revenueByRegionData?.regions) ? revenueByRegionData.regions
      : Array.isArray(revenueByRegionData?.countries) ? revenueByRegionData.countries
      : toRows(revenueByRegionData);

  const regionChartRows = (() => {
    const normalized = regionRows
      .map((item) => ({
        label: item?.region || item?.country || item?.name || "Region",
        revenue: Number(item?.revenue ?? item?.value ?? item?.amount ?? 0),
        percentage: Number(item?.percentage ?? 0),
      }))
      .filter((item) => Number.isFinite(item.revenue) && item.revenue > 0)
      .sort((a, b) => b.revenue - a.revenue);

    if (normalized.length <= 6) return normalized;

    const top6 = normalized.slice(0, 6);
    const rest = normalized.slice(6);
    const othersRevenue = rest.reduce((sum, item) => sum + item.revenue, 0);
    const othersPctRaw = rest.reduce((sum, item) => sum + item.percentage, 0);
    const totalRevenue = normalized.reduce((sum, item) => sum + item.revenue, 0);
    const othersPercentage = othersPctRaw > 0
      ? othersPctRaw
      : (totalRevenue > 0 ? (othersRevenue / totalRevenue) * 100 : 0);

    return [...top6, { label: "Others", revenue: othersRevenue, percentage: othersPercentage }];
  })();

  const regionLabels = regionChartRows.map((item) => item.label);
  const regionValues = regionChartRows.map((item) => item.revenue);
  const regionPercentages = regionChartRows.map((item) => item.percentage);

  const gatewayRows =
    Array.isArray(gatewayRevenueData?.gateways) ? gatewayRevenueData.gateways : toRows(gatewayRevenueData);
  const gatewayLabels = gatewayRows.map((item) => item?.gateway || item?.gateway_name || item?.name || "Gateway");
  const gatewayValues = gatewayRows.map((item) => Number(item?.revenue ?? item?.value ?? item?.amount ?? 0));

  const transactionRows =
    Array.isArray(transactionBreakdownData?.gateways) ? transactionBreakdownData.gateways
      : Array.isArray(transactionBreakdownData?.transactions) ? transactionBreakdownData.transactions
      : toRows(transactionBreakdownData);

  const fmtWeekLabel = (val) => {
    if (!val) return "";
    const s = String(val);
    const wMatch = s.match(/[Ww](?:k\s*)?(\d+)/i);
    if (wMatch) return `Wk ${wMatch[1]}`;
    return s;
  };
  const revTrendTickAmount = 8;
  const revTrendCats = weeklyCategories;
  const hasRevenueTrendData = weeklyRevenueValues.length > 0 || weeklyBookingsValues.length > 0;

  // Pie/donut color palette — revenue tab specific (teal, indigo, amber, rose, emerald, slate)
  const pieColors = ["#0f766e", "#4f46e5", "#f59e0b", "#e11d48", "#059669", "#475569"];

  return (
    <div className={classes.tabStack}>
      {/* KPIs */}
      <Grid gutter={12}>
        {dynamicRevenueKpis.map((item) => (
          <Grid.Col span={{ base: 12, md: 4 }} key={item.label}>
            <AdminKpiCard {...item} />
          </Grid.Col>
        ))}
      </Grid>

      {/* Revenue Trend (area) + Operator-wise Revenue (donut) */}
      <Grid gutter={12}>
        <Grid.Col span={{ base: 12, md: 7 }}>
          <div className={classes.card}>
            <div className={classes.cardHeader}>Weekly Revenue Trend + Bookings</div>
            {hasRevenueTrendData ? (
              <ReactApexChart
                options={{
                  ...chartBase,
                  chart: { ...chartBase.chart, type: "bar" },
                  colors: ["#16a34a", "#1d4ed8"],
                  plotOptions: { bar: { borderRadius: 3, columnWidth: "55%" } },
                  stroke: { width: [0, 2.5], curve: "smooth" },
                  fill: { opacity: [0.85, 1] },
                  xaxis: {
                    categories: revTrendCats,
                    tickAmount: Math.min(revTrendCats.length, revTrendTickAmount),
                    labels: {
                      rotate: -20,
                      formatter: fmtWeekLabel,
                      style: { fontSize: "9px" },
                      hideOverlappingLabels: true,
                    },
                  },
                  yaxis: [
                    {
                      title: { text: "Revenue", style: { fontSize: "10px" } },
                      labels: { style: { fontSize: "10px" }, formatter: formatCompactInr },
                    },
                    {
                      opposite: true,
                      title: { text: "Bookings", style: { fontSize: "10px" } },
                      labels: { style: { fontSize: "10px" }, formatter: (v) => Math.round(v) },
                    },
                  ],
                  tooltip: {
                    shared: true,
                    intersect: false,
                    x: { formatter: (val, { dataPointIndex }) => fmtWeekLabel(revTrendCats[dataPointIndex] ?? val) },
                    y: {
                      formatter: (v, { seriesIndex, dataPointIndex }) => {
                        const row = revenueTrendRows[dataPointIndex] || {};
                        const range = row.start && row.end ? ` (${row.start} to ${row.end})` : "";
                        return seriesIndex === 0
                          ? `Revenue: ₹${Number(v).toLocaleString("en-IN")}${range}`
                          : `Bookings: ${Math.round(Number(v))}${range}`;
                      },
                    },
                  },
                  legend: { position: "top" },
                }}
                series={[
                  { name: "Revenue", type: "bar", data: weeklyRevenueValues },
                  { name: "Bookings", type: "line", data: weeklyBookingsValues },
                ]}
                type="bar"
                height={260}
              />
            ) : (
              <div className={classes.emptyState}>{DASHBOARD_EMPTY_STATE}</div>
            )}
          </div>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 5 }}>
          <div className={classes.card}>
            <div className={classes.cardHeaderRow}>
              <span className={classes.cardHeader}>Operator-wise Revenue</span>
              <AdminInlineFilters
                fromDate={operatorRevDates.startDate}
                toDate={operatorRevDates.endDate}
                setFromDate={(v) => setOperatorRevDates((p) => ({ ...p, startDate: v }))}
                setToDate={(v) => setOperatorRevDates((p) => ({ ...p, endDate: v }))}
              />
            </div>
            {operatorValues.length ? (
              <ReactApexChart
                options={{
                  ...chartBase,
                  labels: operatorLabels,
                  colors: pieColors,
                  plotOptions: {
                    pie: {
                      donut: {
                        size: "62%",
                        labels: {
                          show: true,
                          total: {
                            show: true,
                            label: "Total",
                            fontSize: "12px",
                            fontFamily: FONT,
                            formatter: () =>
                              fmtDonutCenterTotal(operatorPieTotal, operatorPieUsesPercent),
                          },
                        },
                      },
                    },
                  },
                  legend: { position: "bottom", fontSize: "11px", fontFamily: FONT },
                  tooltip: {
                    y: {
                      formatter: (v) =>
                        operatorPieUsesPercent
                          ? `${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 0 })}%`
                          : `₹${Number(v).toLocaleString("en-IN")}`,
                    },
                  },
                }}
                series={operatorValues}
                type="donut"
                height={260}
              />
            ) : (
              <div className={classes.emptyState}>{DASHBOARD_EMPTY_STATE}</div>
            )}
          </div>
        </Grid.Col>
      </Grid>

      {/* Vendor donut + Region horizontal bar + Gateway horizontal bar */}
      <Grid gutter={12}>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <div className={classes.card}>
            <div className={classes.cardHeaderRow}>
              <span className={classes.cardHeader}>Vendor-wise Revenue</span>
              <AdminInlineFilters
                fromDate={vendorRevDates.startDate}
                toDate={vendorRevDates.endDate}
                setFromDate={(v) => setVendorRevDates((p) => ({ ...p, startDate: v }))}
                setToDate={(v) => setVendorRevDates((p) => ({ ...p, endDate: v }))}
              />
            </div>
            {vendorValues.length ? (
              <ReactApexChart
                options={{
                  ...chartBase,
                  labels: vendorLabels,
                  colors: pieColors,
                  plotOptions: { pie: { donut: { size: "64%" } } },
                  legend: { position: "bottom", fontSize: "11px", fontFamily: FONT },
                }}
                series={vendorValues}
                type="donut"
                height={240}
              />
            ) : (
              <div className={classes.emptyState}>{DASHBOARD_EMPTY_STATE}</div>
            )}
          </div>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <div className={classes.card}>
            <div className={classes.cardHeader}>Revenue by Country</div>
            {regionValues.length ? (
              <ReactApexChart
                options={{
                  ...chartBase,
                  chart: { ...chartBase.chart, type: "bar" },
                  plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: "55%" } },
                  colors: ["#2563eb"],
                  xaxis: {
                    categories: regionLabels,
                    tickAmount: 4,
                    labels: { formatter: formatCompactInr, style: { fontSize: "9px" }, hideOverlappingLabels: true },
                    axisBorder: { show: false },
                    axisTicks: { show: false },
                  },
                  yaxis: {
                    labels: { formatter: (v) => truncate(v, 14), style: { fontSize: "10px", fontFamily: FONT } },
                  },
                  tooltip: {
                    y: {
                      formatter: (v, { dataPointIndex }) => {
                        const rp = regionPercentages[dataPointIndex];
                        const pct =
                          rp === undefined || rp === null || !Number.isFinite(Number(rp))
                            ? "—"
                            : `${Number(rp).toFixed(2)}%`;
                        return `₹${Number(v).toLocaleString("en-IN")} (${pct})`;
                      },
                    },
                  },
                }}
                series={[{ name: "Revenue", data: regionValues }]}
                type="bar"
                height={240}
              />
            ) : (
              <div className={classes.emptyState}>{DASHBOARD_EMPTY_STATE}</div>
            )}
          </div>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <div className={classes.card}>
            <div className={classes.cardHeaderRow}>
              <span className={classes.cardHeader}>Gateway-wise Revenue</span>
              <AdminInlineFilters
                fromDate={gatewayRevDates.startDate}
                toDate={gatewayRevDates.endDate}
                setFromDate={(v) => setGatewayRevDates((p) => ({ ...p, startDate: v }))}
                setToDate={(v) => setGatewayRevDates((p) => ({ ...p, endDate: v }))}
              />
            </div>
            {/* Horizontal bar — gateway names are long, avoids x-axis overlap */}
            {gatewayValues.length ? (
              <ReactApexChart
                options={{
                  ...chartBase,
                  chart: { ...chartBase.chart, type: "bar" },
                  plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: "55%" } },
                  colors: ["#7c3aed"],
                  xaxis: {
                    categories: gatewayLabels,
                    tickAmount: 4,
                    labels: { formatter: formatCompactInr, style: { fontSize: "9px" }, hideOverlappingLabels: true },
                    axisBorder: { show: false },
                    axisTicks: { show: false },
                  },
                  yaxis: {
                    labels: { formatter: (v) => truncate(v, 16), style: { fontSize: "10px", fontFamily: FONT } },
                  },
                  tooltip: { y: { formatter: (v) => `₹${Number(v).toLocaleString("en-IN")}` } },
                }}
                series={[{ name: "Revenue", data: gatewayValues }]}
                type="bar"
                height={240}
              />
            ) : (
              <div className={classes.emptyState}>{DASHBOARD_EMPTY_STATE}</div>
            )}
          </div>
        </Grid.Col>
      </Grid>

      {/* Transaction Breakdown Table */}
      <div className={classes.card}>
        <div className={classes.cardHeader}>Transaction Breakdown by Gateway</div>
        {transactionRows.length ? (
          <Table className={classes.table} horizontalSpacing="sm" verticalSpacing={7}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Gateway</Table.Th>
                <Table.Th>Success %</Table.Th>
                <Table.Th>Failed %</Table.Th>
                <Table.Th>Pending %</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {transactionRows.map((item, idx) => (
                <Table.Tr key={idx}>
                  <Table.Td>{item?.gateway || item?.gateway_name || item?.name || "—"}</Table.Td>
                  <Table.Td>
                    <Badge className={classes.badgeSuccess}>
                      {formatOptionalPercent(item?.success_percentage ?? item?.success_percent ?? item?.success_rate)}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge className={classes.badgeFailed}>
                      {formatOptionalPercent(item?.failed_percentage ?? item?.failed_percent ?? item?.failure_rate)}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge className={classes.badgePending}>
                      {formatOptionalPercent(item?.pending_percentage ?? item?.pending_percent ?? item?.pending_rate)}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        ) : (
          <div className={classes.emptyState}>{DASHBOARD_EMPTY_STATE}</div>
        )}
      </div>
    </div>
  );
};

export default AdminRevenueTab;
